interface Props {
    variant: 'red' | 'black';
    selected?: boolean;
    onClick?: () => void;
    /** Tailwind classes for size; defaults to w-10 h-10 */
    sizeClass?: string;
    'aria-label'?: string;
}

const PALETTE = {
    // Chip colours follow the coinflip team palette: side 1 = pink (--p1),
    // side 2 = green (--p2), matching the avatar rings on the page.
    red: {
        rim: '#a82d6e',
        rimDeep: '#5a1338',
        face: '#ff5fae',
        faceLight: '#ffa6d6',
        glow: 'var(--p1)',
        ringClass: 'ring-p1',
    },
    black: {
        rim: '#1f7a4d',
        rimDeep: '#0c3a24',
        face: '#46e08a',
        faceLight: '#9bf0c0',
        glow: 'var(--p2)',
        ringClass: 'ring-p2',
    },
};

const Chip = ({
    variant,
    selected,
    onClick,
    sizeClass = 'w-10 h-10',
    ...rest
}: Props) => {
    const p = PALETTE[variant];
    const gradRim = `rim-${variant}`;
    const gradFace = `face-${variant}`;
    const gradHi = `hi-${variant}`;

    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={rest['aria-label']}
            className={`${sizeClass} rounded-full grid place-items-center transition-all ${
                selected
                    ? `ring-2 ring-offset-2 ring-offset-bg_mica ${p.ringClass}`
                    : 'opacity-70 hover:opacity-100'
            }`}
            style={{
                boxShadow: selected ? `0 0 18px -2px ${p.glow}` : 'none',
            }}
        >
            <svg viewBox="0 0 40 40" className="w-full h-full">
                <defs>
                    <radialGradient id={gradRim} cx="0.5" cy="0.35" r="0.65">
                        <stop offset="0" stopColor={p.rim} />
                        <stop offset="1" stopColor={p.rimDeep} />
                    </radialGradient>
                    <radialGradient id={gradFace} cx="0.32" cy="0.28" r="0.7">
                        <stop offset="0" stopColor={p.faceLight} />
                        <stop offset="0.6" stopColor={p.face} />
                        <stop offset="1" stopColor={p.rim} />
                    </radialGradient>
                    <radialGradient id={gradHi} cx="0.5" cy="0.5" r="0.5">
                        <stop offset="0" stopColor="#ffffff" stopOpacity="0.25" />
                        <stop offset="0.6" stopColor="#ffffff" stopOpacity="0.06" />
                        <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
                    </radialGradient>
                </defs>

                {/* Outer rim */}
                <circle cx="20" cy="20" r="19" fill={`url(#${gradRim})`} />

                {/* 8 cream-coloured edge notches (typical casino chip pattern) */}
                {Array.from({ length: 8 }).map((_, i) => {
                    const angle = (i * 360) / 8;
                    return (
                        <rect
                            key={i}
                            x="18.5"
                            y="0.5"
                            width="3"
                            height="5.5"
                            rx="1"
                            fill="#f4ead0"
                            opacity="0.92"
                            transform={`rotate(${angle} 20 20)`}
                        />
                    );
                })}

                {/* Thin separator ring between rim & face */}
                <circle
                    cx="20"
                    cy="20"
                    r="14"
                    fill="none"
                    stroke="rgba(0,0,0,0.45)"
                    strokeWidth="0.8"
                />

                {/* Face */}
                <circle cx="20" cy="20" r="13.5" fill={`url(#${gradFace})`} />

                {/* Inner decorative ring */}
                <circle
                    cx="20"
                    cy="20"
                    r="9.5"
                    fill="none"
                    stroke="rgba(255,255,255,0.18)"
                    strokeWidth="0.6"
                    strokeDasharray="2 1.5"
                />

                {/* Top-light glossy highlight */}
                <ellipse
                    cx="16"
                    cy="13"
                    rx="9"
                    ry="5"
                    fill={`url(#${gradHi})`}
                />

                {/* Outer edge shadow */}
                <circle
                    cx="20"
                    cy="20"
                    r="19"
                    fill="none"
                    stroke="rgba(0,0,0,0.55)"
                    strokeWidth="0.6"
                />
            </svg>
        </button>
    );
};

export default Chip;
